import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaPlane,
  FaHotel,
  FaBus,
  FaTrain,
  FaUmbrellaBeach,
  FaCar,
  FaUtensils,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaYoutube
} from 'react-icons/fa';
import { useAdmin } from '../../../contexts/AdminContext';
import { useWebsiteSettings } from '../../contexts/WebsiteSettingsContext';
import { getNavIcon } from './navIconMap';

const iconMap = {
  flight: FaPlane,
  train: FaTrain,
  hotel: FaHotel,
  restaurant: FaUtensils,
  bus: FaBus,
  car: FaCar,
  holiday: FaUmbrellaBeach
};

const socialIcons = { facebook: FaFacebook, twitter: FaTwitter, instagram: FaInstagram, linkedin: FaLinkedin, youtube: FaYoutube };

const Footer = () => {
  const { companySettings } = useAdmin();
  const { settings, nav, footer, getImageUrl } = useWebsiteSettings();

  const logo = footer?.logo || footer?.logoUrl || settings?.header?.logo || settings?.header?.logoUrl || companySettings?.logo;
  const logoUrl = logo ? getImageUrl(logo) : '';
  const companyName = footer?.companyName || settings?.name || companySettings?.companyName || 'BookYatri';
  const description = footer?.description || `${companyName} is your trusted online travel agency offering comprehensive travel solutions including flights, hotels, buses, trains, and holiday packages.`;
  const copyright = footer?.copyright || `© ${new Date().getFullYear()} ${companyName}. All rights reserved.`;
  const social = footer?.socialMedia || {};

  const columnLinks = (title) => {
    const col = (footer?.columns || []).find(c => (c.title || '').toLowerCase() === title.toLowerCase());
    return col?.links?.filter(l => l.label) || null;
  };

  const customServices = columnLinks('Our Services');
  const acceptedPayments = (footer?.acceptedPayments || []).filter(p => p.image);

  const services = (nav || []).map((n) => ({
    name: n.label,
    path: n.url,
    icon: iconMap[n.icon?.toLowerCase()] || getNavIcon(n.icon) || FaPlane
  }));

  const quickLinks = columnLinks('Quick Links') || [
    { label: 'About Us', url: '/about' },
    { label: 'Contact Us', url: '/contact' },
    { label: 'Terms & Conditions', url: '/terms' },
    { label: 'Privacy Policy', url: '/privacy' },
    { label: 'FAQ', url: '/faq' },
    { label: 'Blog', url: '/blog' }
  ];

  const supportLinks = columnLinks('Support') || [
    { label: 'Customer Support', url: '/support' },
    { label: 'Booking Help', url: '/help' },
    { label: 'Cancellation Policy', url: '/cancellation' },
    { label: 'Refund Policy', url: '/refund' },
    { label: 'Travel Insurance', url: '/insurance' },
    { label: 'Group Bookings', url: '/group-booking' }
  ];

  const contactPhone = footer?.phone || '';
  const contactEmail = footer?.email || '';
  const contactAddress = footer?.address || '';

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center overflow-hidden">
                {logoUrl ? (
                  <img src={logoUrl} alt="Company Logo" className="w-full h-full object-contain" />
                ) : (
                  <FaPlane className="text-white text-xl" />
                )}
              </div>
              <div>
                <h3 className="text-xl font-bold">{companyName}</h3>
              </div>
            </div>
            <p className="text-gray-400 text-sm">{description}</p>
            <div className="space-y-2">
              {contactPhone && (
                <div className="flex items-center space-x-2 text-sm">
                  <FaPhone className="text-primary-400" />
                  <span>{contactPhone}</span>
                </div>
              )}
              {contactEmail && (
                <div className="flex items-center space-x-2 text-sm">
                  <FaEnvelope className="text-primary-400" />
                  <span>{contactEmail}</span>
                </div>
              )}
              {contactAddress && (
                <div className="flex items-center space-x-2 text-sm">
                  <FaMapMarkerAlt className="text-primary-400" />
                  <span>{contactAddress}</span>
                </div>
              )}
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">Our Services</h4>
            {customServices ? (
              <ul className="space-y-2">
                {customServices.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.url || '#'}
                      className="text-gray-400 hover:text-white transition-colors text-sm"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
            <div className="grid grid-cols-1 gap-2">
              {services.map((s) => {
                const Icon = s.icon;
                return (
                  <Link
                    key={s.name}
                    to={s.path}
                    className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    <Icon className="text-primary-400" />
                    <span>{s.name}</span>
                  </Link>
                );
              })}
            </div>
            )}
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.label || link.name}>
                  <Link
                    to={link.url || link.path || '#'}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {link.label || link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">Support</h4>
            <ul className="space-y-2">
              {supportLinks.map((link) => (
                <li key={link.label || link.name}>
                  <Link
                    to={link.url || link.path || '#'}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {link.label || link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <h5 className="text-sm font-semibold mb-3">Follow Us</h5>
              <div className="flex space-x-3">
                {Object.entries(socialIcons).map(([key, Icon]) => {
                  const url = social[key];
                  if (!url) return null;
                  return (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center hover:bg-primary-600 transition-colors"
                      aria-label={key}
                    >
                      <Icon className="text-sm" />
                    </a>
                  );
                })}
                {!Object.values(social).some(Boolean) && (
                  <>
                    <a href="#" className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center hover:bg-primary-600 transition-colors" aria-label="Facebook"><FaFacebook className="text-sm" /></a>
                    <a href="#" className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center hover:bg-primary-600 transition-colors" aria-label="Twitter"><FaTwitter className="text-sm" /></a>
                    <a href="#" className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center hover:bg-primary-600 transition-colors" aria-label="Instagram"><FaInstagram className="text-sm" /></a>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-gray-400">{copyright}</div>
            <div className="flex items-center space-x-3 text-sm text-gray-400">
              <span className="whitespace-nowrap">We accept:</span>
              <div className="flex items-center gap-2.5 flex-wrap">
                {acceptedPayments.length > 0 ? (
                  acceptedPayments.map((p, i) => (
                    <img
                      key={`${p.name || 'pay'}-${i}`}
                      src={getImageUrl(p.image)}
                      alt={p.name || 'Payment method'}
                      title={p.name || 'Payment method'}
                      loading="lazy"
                      className="h-8 w-auto max-w-[4.5rem] min-w-[2.75rem] object-contain rounded-md bg-white px-1.5 py-0.5 shadow-sm"
                    />
                  ))
                ) : (
                  <>
                    <div className="w-11 h-8 bg-blue-600 rounded-md text-sm flex items-center justify-center text-white font-bold">V</div>
                    <div className="w-11 h-8 bg-red-600 rounded-md text-sm flex items-center justify-center text-white font-bold">M</div>
                    <div className="w-11 h-8 bg-yellow-600 rounded-md text-sm flex items-center justify-center text-white font-bold">A</div>
                    <div className="w-11 h-8 bg-green-600 rounded-md text-sm flex items-center justify-center text-white font-bold">R</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
